import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { POLYLINE_HEIGHT, STARTING_POSITION } from './dynamic-polyline.constants';
import { CommonModule } from '@angular/common';
import { PolylineComponent } from '../polyline/polyline.component';


@Component({
    selector: 'finder-dynamic-polyline',
    templateUrl: './dynamic-polyline.component.html',
    styleUrls: ['./dynamic-polyline.component.scss'],
    standalone: true,
    imports: [ CommonModule, PolylineComponent ],
})
export class DynamicPolylineComponent implements AfterViewInit, OnDestroy {
    @Input() starting: STARTING_POSITION = STARTING_POSITION.HIGH;
    @Input() transition = 50; // Should be a value less than 100
    @Input() color = 'white';
    @Input() width = '1';

    // We need to wait for both of these values to be set before creating the element
    polyline: string;
    viewBox: string;

    private destroy$ = new Subject<void>();

    constructor(private elementRef: ElementRef, private viewportService: ViewportDetectionService) { }

    ngAfterViewInit(): void {
        this.generateSvg();

        // Listen for resize and redraw the line
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.generateSvg();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /* istanbul ignore next */
    getElementReference() {
        // This is used to make testing easier
        return this.elementRef.nativeElement;
    }

    generateSvg() {
        this.generateViewBox();
        this.generatePolyline();
    }

    private generateViewBox() {
        /**
         * The viewbox controls how big the canvas is. Since the canvas
         * size will change we need to make the viewbox dynamic
         */
        this.viewBox = `0 0 ${this.getElementReference().offsetWidth} ${POLYLINE_HEIGHT}`;
    }

    private generatePolyline() {
        // Start with the basic properties
        this.polyline = `${this.color}:${this.width}`;
        // 'x,y', 'x,y'
        const points = [];
        let x = 0;
        let y = 0;
        // Shortcut to store values
        const storeVals = () => {
            points.push(`${x},${y}`);
        };
        const containerWidth = this.getElementReference().offsetWidth;

        // Pick the starting position
        if (this.starting === STARTING_POSITION.HIGH) {
            storeVals();
        } else {
            y = POLYLINE_HEIGHT;
            storeVals();
        }

        // Get the transtion point
        x += Math.ceil(containerWidth * (this.transition/100));
        storeVals();

        // Angle the line
        x += POLYLINE_HEIGHT;
        if (this.starting === STARTING_POSITION.HIGH) {
            // Angle the line down
            y += POLYLINE_HEIGHT;
        } else {
            // Angle line up
            y -= POLYLINE_HEIGHT;
        }
        storeVals();

        // Draw to the end of the component
        // Subtract 20 for the angle adjustment
        x += Math.ceil(containerWidth * ((100 - this.transition)/100)) - POLYLINE_HEIGHT;
        storeVals();

        // Write out our line
        this.polyline += ':' + points.join(' ');
    }
}
